import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import './EstimatesList.css';

function EstimatesList({ onLogout }) {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchEstimates = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/jobcards', {
        params: {
          documentType: 'Estimate',
          search,
          ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setEstimates(response.data.jobCards || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load estimates.');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    fetchEstimates();
  }, [fetchEstimates]);

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return '-';
    }

    return new Date(dateValue).toLocaleDateString('en-GB');
  };

  const formatMoney = (amount) => `Tk ${Number(amount || 0).toFixed(2)}`;
  const getApprovalLabel = (estimate) => {
    if (String(estimate.ClientApprovalStatus || '').toLowerCase() === 'approved') {
      return 'Approved';
    }

    if (estimate.Status === 'Closed') {
      return 'Pending Client';
    }

    return '-';
  };

  return (
    <div className="home-container app-shell-fixed">
      <Sidebar onLogout={onLogout} />
      <main className="estimates-page app-fixed-main app-scroll-layout">
        <header className="estimates-header app-page-header">
          <div className="app-title-group">
            <button className="app-back-button" onClick={() => navigate('/')} aria-label="Back to home" />
            <div className="app-title-copy">
              <h1>Estimates</h1>
              <p>Create quotes with vehicle, client, mileage, parts, and labor costs.</p>
            </div>
          </div>
          <div className="app-header-actions">
            <button className="estimates-refresh-button" type="button" onClick={fetchEstimates}>
              Refresh
            </button>
            <button className="estimates-new-button" type="button" onClick={() => navigate('/estimates/new')}>
              + New Estimate
            </button>
          </div>
        </header>

        <section className="estimates-toolbar" aria-label="Estimate filters">
          <label className="estimates-search">
            <span aria-hidden="true">{'\u2315'}</span>
            <input
              type="search"
              placeholder="Search estimate number, client, vehicle"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label="Estimate status">
            <option value="all">All Status</option>
            <option value="Created">Open</option>
            <option value="Closed">Closed</option>
          </select>
        </section>

        <div className="app-scroll-results">
          {loading ? (
            <div className="estimates-feedback">Loading estimates...</div>
          ) : error ? (
            <div className="estimates-feedback estimates-error">{error}</div>
          ) : estimates.length === 0 ? (
            <div className="estimates-empty">
              <div className="estimates-empty-icon">{'\u{1F4C8}'}</div>
              <h2>No Estimates Found</h2>
              <p>Create a new estimate to start quoting parts and labor for a vehicle.</p>
            </div>
          ) : (
            <div className="estimates-table-wrap app-grid-scroll">
              <table className="estimates-table">
                <thead>
                  <tr>
                    <th>Estimate #</th>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Vehicle</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Approval</th>
                    <th>Note</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {estimates.map((estimate) => (
                    <tr key={estimate.JobCardId}>
                      <td>#{estimate.JobNumber || estimate.JobCardId}</td>
                      <td>{formatDate(estimate.CreatedDate)}</td>
                      <td>{estimate.ClientName || '-'}</td>
                      <td>{[estimate.CarNumber, estimate.Year, estimate.Make, estimate.Model].filter(Boolean).join(' ') || '-'}</td>
                      <td>{formatMoney(estimate.TotalAmount)}</td>
                      <td>
                        <span className={`estimate-status estimate-status-${String(estimate.Status || '').toLowerCase()}`}>
                          {estimate.Status || 'Created'}
                        </span>
                      </td>
                      <td>
                        {String(estimate.ClientApprovalStatus || '').toLowerCase() === 'approved' ? (
                          <div className="estimate-approval-cell">
                            <span className="estimate-approval-mark">Approved</span>
                            <small>{formatDate(estimate.ClientApprovedAt)}</small>
                          </div>
                        ) : (
                          <span className="estimate-approval-pending">{getApprovalLabel(estimate)}</span>
                        )}
                      </td>
                      <td className="estimate-note-cell" title={estimate.ClientApprovalNote || ''}>
                        {estimate.ClientApprovalNote ? (
                          <span className="estimate-note-text">{estimate.ClientApprovalNote}</span>
                        ) : '-'}
                      </td>
                      <td>
                        <button type="button" className="estimate-open-button" onClick={() => navigate(`/estimates/${estimate.JobCardId}/edit`)}>
                          {estimate.Status === 'Closed' ? 'View' : 'Open'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default EstimatesList;
